/**
 * The unselect step and the count confirmation, ISSUE-197 R3, R4 and R6.
 *
 * Every candidate arrives checked. Unchecking one drops it from what will be
 * sent, and the headline recounts on the same render, because both come from
 * `buildInviteSelection` and `inviteConfirmHeadline` rather than from the
 * candidate list. `Send` hands `selection.selectedUserIds` to the mutation,
 * never `props.candidates`.
 *
 * R5, and this is the whole reason the sent state is a single sentence: the
 * response carries `alreadyMemberCount`, `alreadyInvitedCount` and
 * `skippedCount`, and none of them is ever rendered. A fan who was blocked is
 * dropped with no signal at all, per POOLS-10. Only `sentCount` reaches the
 * screen.
 *
 * Rows already in the pool or already invited are shown disabled after the
 * selectable ones (ISSUE-1097 R4). They never enter `buildInviteSelection`,
 * so the headline count and the send body ignore them.
 */
import { useInviteBulkSend } from "@sportsmet/pool-sdk/private";
import {
  Button,
  Checkbox,
  InlineBanner,
  OVERLAY_FOOTER_LAYOUT,
} from "@sportsmet/ui";
import { onlineManager } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  buildInviteSelection,
  inviteConfirmHeadline,
  inviteSentNote,
  toggleInviteCandidate,
  type typeInviteCandidate,
} from "./inviteBulkSelection";
import {
  INVITE_BULK_CANCEL_LABEL,
  INVITE_BULK_DONE_LABEL,
  INVITE_BULK_SEND_LABEL,
  INVITE_ROW_IN_POOL_LABEL,
  INVITE_ROW_INVITED_LABEL,
} from "./inviteSheetCopy";

export type typeInviteAlreadyInRow = {
  userId: string;
  displayName: string;
  relation: "member" | "invited";
};

export type typeInviteBulkConfirmProps = {
  poolId: string;
  source: "pool" | "chat";
  sourceId: string;
  sourceName: string;
  candidates: readonly typeInviteCandidate[];
  alreadyIn?: readonly typeInviteAlreadyInRow[];
  onDone: () => void;
};

export const InviteBulkConfirm = ({
  poolId,
  source,
  sourceId,
  sourceName,
  candidates,
  alreadyIn = [],
  onDone,
}: typeInviteBulkConfirmProps): ReactNode => {
  const [unselectedUserIds, setUnselectedUserIds] = useState<string[]>([]);
  const [sentCount, setSentCount] = useState<number | null>(null);
  const send = useInviteBulkSend();

  const selection = buildInviteSelection({ candidates, unselectedUserIds });

  async function handleSend(): Promise<void> {
    let result: Awaited<ReturnType<typeof send.mutateAsync>>;
    try {
      result = await send.mutateAsync({
        poolId,
        source,
        sourceId,
        selectedUserIds: selection.selectedUserIds,
      });
    } catch {
      // A throw means the response never became an envelope at all. The
      // MutationCache handler in `../../queryClient.ts` raises the offline
      // modal for that, and it runs before this line does, so an
      // unconditional call here would put two identical events on a bus whose
      // whole value is that its contents are countable.
      //
      // It is NOT unconditional, though. That handler is
      // `if (onlineManager.isOnline()) showOfflineError()`, pinned by
      // `queryClient.test.ts`, because at the page level a false online flag
      // is already answered by the persistent `OfflineBanner` in `AppShell`.
      // That reasoning does not carry into this sheet. The banner is a
      // page-level state that was very likely on screen BEFORE `Send` was
      // pressed, it says nothing about this send, and while the sheet is open
      // Radix has it inert behind the scrim. Left to the handler alone, a send
      // that throws with the flag already false is answered by nothing at all:
      // the button returns to idle, the selection is still on screen, and the
      // invites never went. So this catch covers exactly the half the handler
      // skips and stays quiet for the half it owns, which keeps one failure to
      // one event either way.
      //
      // The `return` is the part nothing else does: stop, so the sent note
      // below never renders off an unassigned `result`.
      if (!onlineManager.isOnline()) showOfflineError();
      return;
    }
    // A refused send must not be reported as a send. Without this the sent
    // note would render off a null `data` and read "No invites went out."
    // beside an error modal that says the opposite.
    if (showApiError(result)) return;
    setSentCount(result.data?.sentCount ?? 0);
  }

  if (sentCount !== null) {
    return (
      <div className="flex flex-col gap-3">
        <InlineBanner
          variant="success"
          message={inviteSentNote({ sentCount })}
        />
        <div className={OVERLAY_FOOTER_LAYOUT}>
          <Button
            variant="secondary"
            onClick={onDone}
            data-testid="invite-bulk-done"
          >
            {INVITE_BULK_DONE_LABEL}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="invite-bulk-confirm">
      <p
        className="text-body-strong text-ink"
        data-testid="invite-confirm-headline"
      >
        {inviteConfirmHeadline({
          selectedCount: selection.selectedCount,
          sourceName,
        })}
      </p>
      <ul className="flex flex-col gap-1">
        {selection.rows.map((row) => (
          <li key={row.userId} data-testid={`invite-candidate-${row.userId}`}>
            <Checkbox
              label={row.displayName}
              checked={row.isSelected}
              onCheckedChange={() => {
                setUnselectedUserIds(
                  toggleInviteCandidate({
                    unselectedUserIds,
                    userId: row.userId,
                  }),
                );
              }}
            />
          </li>
        ))}
        {alreadyIn.map((row) => (
          <li key={row.userId} data-testid={`invite-already-in-${row.userId}`}>
            <Checkbox
              label={row.displayName}
              description={
                row.relation === "member"
                  ? INVITE_ROW_IN_POOL_LABEL
                  : INVITE_ROW_INVITED_LABEL
              }
              checked={false}
              disabled
            />
          </li>
        ))}
      </ul>
      <div className={OVERLAY_FOOTER_LAYOUT}>
        <Button
          variant="secondary"
          onClick={onDone}
          data-testid="invite-bulk-cancel"
        >
          {INVITE_BULK_CANCEL_LABEL}
        </Button>
        <Button
          variant="primary"
          disabled={!selection.canSend}
          loading={send.isPending}
          loadingLabel={INVITE_BULK_SEND_LABEL}
          onClick={() => {
            void handleSend();
          }}
          data-testid="invite-bulk-send"
        >
          {INVITE_BULK_SEND_LABEL}
        </Button>
      </div>
    </div>
  );
};
